import React from "react";

import CircleIcon from "@mui/icons-material/Circle";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import Chip from "@mui/material/Chip";

import {
  OnboardingStatus,
  RegistrationStatus,
  Sentiment,
  TransitionStatus,
} from "@fartherfinance/frontend/api/TransitionWorkflows/Types";

import {
  onboardingStatusMap,
  registrationStatusMap,
  sentimentMap,
  transitionStatusMap,
} from "../utils";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";

import styles from "./TransitionStatusChip.module.css";

type TransitionStatusVariant = {
  variant: "transition";
  status: TransitionStatus;
};

type OnboardingStatusVariant = {
  variant: "onboarding";
  status: OnboardingStatus;
};

type RegistrationStatusVariant = {
  variant: "registration";
  status: RegistrationStatus;
};

type SentimentVariant = {
  variant: "sentiment";
  status: Sentiment;
};

type TransitionStatusUnion =
  | TransitionStatusVariant
  | OnboardingStatusVariant
  | RegistrationStatusVariant
  | SentimentVariant;

const getStatusLabel = ({ status, variant }: TransitionStatusUnion) => {
  if (variant === "onboarding") {
    return onboardingStatusMap[status];
  }

  if (variant === "transition") {
    return transitionStatusMap[status];
  }

  if (variant === "sentiment") {
    return sentimentMap[status];
  }

  if (variant === "registration") {
    return registrationStatusMap[status];
  }

  return status;
};

const getStatusClassName = ({ status, variant }: TransitionStatusUnion) => {
  if (variant === "onboarding") {
    if (status === "LaunchComplete") {
      return styles.success;
    }

    if (status === "Delayed") {
      return styles.warning;
    }

    if (
      status === "InRecruiting" ||
      status === "KickoffComplete" ||
      status === "TransitionInProgress"
    ) {
      return styles.info;
    }
  }

  if (variant === "transition") {
    if (status === "OnTrack") {
      return styles.info;
    }
    if (status === "Delayed") {
      return styles.warning;
    }
    if (status === "OffTrack") {
      return styles.warning;
    }
  }

  if (variant === "sentiment") {
    if (status === "Good") {
      return styles.success;
    }
    if (status === "Mixed" || status === "Low") {
      return styles.waiting;
    }
    if (status === "AtRisk") {
      return styles.warning;
    }
  }

  if (variant === "registration") {
    if (status === "Registered" || status === "DualRegistered") {
      return styles.success;
    }
    if (status === "NotRegistered") {
      return styles.warning;
    }
  }

  return styles.default;
};

const getStatusIcon = ({ status, variant }: TransitionStatusUnion) => {
  if (variant === "onboarding") {
    if (status === "LaunchComplete") {
      return <CircleIcon className={styles.chipIcon} />;
    }

    return <CircleOutlinedIcon className={styles.chipIcon} />;
  }

  if (variant === "transition") {
    return <CircleOutlinedIcon className={styles.chipIcon} />;
  }

  if (variant === "registration") {
    if (status === "Registered" || status === "DualRegistered") {
      return <CircleIcon className={styles.chipIcon} />;
    }

    return <CircleOutlinedIcon className={styles.chipIcon} />;
  }

  return null;
};

type TransitionStatusChipProps = TransitionStatusUnion;

const TransitionStatusChip: React.FC<TransitionStatusChipProps> = (
  statusData
) => {
  const statusIcon = getStatusIcon(statusData);

  return (
    <Chip
      classes={{
        root: toClassName(styles.chip, getStatusClassName(statusData)),
        label: styles.chipLabel,
        ...(statusIcon && { icon: styles.chipIcon }),
      }}
      variant="outlined"
      label={getStatusLabel(statusData)}
      {...(statusIcon && { icon: statusIcon })}
    />
  );
};

export default TransitionStatusChip;
